import React, { useState, useEffect, memo } from 'react';
import { Drawer, List, ListItemButton, ListItemIcon, ListItemText, styled, useMediaQuery } from '@mui/material';

import { SideBarMenuItem } from 'app/store/types';
import BaseLeftSideBarHeader from './BaseLeftSideBarHeader';
import { DRAWER_WIDTH_CLOSE, DRAWER_WIDTH_OPEN } from 'app/utils/constants';

export interface BaseLeftSideBarProps {
	menuItems: SideBarMenuItem[];
	selected?: string | number;
	onSelect: (item: SideBarMenuItem) => void;
	onClickNavigate: (item: SideBarMenuItem) => void;
	widthOpen?: number;
	widthClosed?: number;
	autoCollapseBreakpoint?: string;
}

// Drawer Styling
const StyledDrawer = styled(Drawer, {
	shouldForwardProp: prop => prop !== 'open'
})<{ open?: boolean }>(({ theme, open }) => ({
	width: open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSE,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	'& .MuiDrawer-paper': {
		width: open ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSE,
		background: `linear-gradient(90deg, #204321 0%, ${theme.palette.secondary.main} 64px, ${theme.palette.secondary.main} 64px, #323836 64px, ${theme.palette.primary.main} 100%)`,
		boxShadow: theme.shadows[3],
		color: '#fff',
		transition: 'width 0.3s',
		overflowX: 'hidden'
	}
}));

const BaseLeftSideBar = ({
	menuItems,
	selected,
	onSelect,
	onClickNavigate,
	autoCollapseBreakpoint = '(max-width:1200px)'
}: BaseLeftSideBarProps) => {
	const isMediumDown = useMediaQuery(autoCollapseBreakpoint);
	const [open, setOpen] = useState<boolean>(true);
	const [isPinned, setIsPinned] = useState(false);
	
	// Auto adjust sidebar on screen resize
	useEffect(() => {
		if (isMediumDown) {
			setIsPinned(false);
			setOpen(false); // auto collapse (icons only)
		} else {
			setIsPinned(true);
			setOpen(true); // full mode
		}
	}, [isMediumDown]);

	const toggleDrawer = () => {
		if (isPinned) {
			setIsPinned(false);
			setOpen(false);
			return;
		}
		setIsPinned(true);
		setOpen(true);
	};

	const hoverOpen = () => !isPinned && setOpen(true);
	const hoverClose = () => !isPinned && setOpen(false);

	return (
		<StyledDrawer variant="permanent" open={open} onMouseEnter={hoverOpen} onMouseLeave={hoverClose}>
			{/* HEADER */}
			<BaseLeftSideBarHeader
				open={open}
				isPinned={isPinned}
				hoverOpen={hoverOpen}
				hoverClose={hoverClose}
				toggleDrawer={toggleDrawer}
			/>

			{/* MENU */}
			<List
				sx={
					open
						? {}
						: {
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'center',
								alignItems: 'center'
						  }
				}
			>
				{menuItems.map((item, index) => {
					return (
						<ListItemButton
							key={index}
							selected={selected === item.value}
							disableRipple
							onClick={() => {
								onSelect?.(item);
								onClickNavigate?.(item);
							}}
							sx={{
								px: 2,
								py: 1.2,
								height: '40px',
								width: open ? 'inherit' : '40px',
								margin: '6px 0',
								borderRadius: open ? 0 : '20px',
								position: open ? 'static' : 'relative',
								'&.Mui-selected': {
									background: 'linear-gradient(#86F750CC 0%, #4D8F2ECC 100%)',
									color: '#000'
								},
								'&.Mui-selected:hover': {
									background: 'linear-gradient(#86F750CC 0%, #4D8F2ECC 100%)',
									color: '#000'
								},
								'&:hover': {
									background: 'rgba(255,255,255,0.15)'
								}
							}}
						>
							<ListItemIcon
								sx={{
									color: '#fff',
									minWidth: 40,
									top: open ? 0 : '50%',
									left: open ? 0 : '50%',
									transform: open ? 'none' : 'translate(-50%, -50%)',
									padding: open ? 0 : '10px',
									position: open ? 'static' : 'absolute'
								}}
							>
								{item.icon}
							</ListItemIcon>

							{open && <ListItemText sx={{ marginLeft: '20px' }} primary={item.label} />}
						</ListItemButton>
					);
				})}
			</List>
		</StyledDrawer>
	);
};

export default memo(BaseLeftSideBar);
